/**
 * @file Especialização CreditCard do pipeline Input.
 * @module components/create/creditCard
 * @see module:components/create/input
 */

/**
 * Normaliza nomes/aliases de bandeira para as chaves usadas pelo validador.
 *
 * O runtime mantém aliases históricos como `DinersClub`, `DinnersClub` e
 * `Mastercards` por compatibilidade de entrada.
 *
 * @param {*} card Nome recebido.
 * @returns {string} Nome normalizado.
 */
const normalizeCardName = (card) => {
  const normalized = String(card ?? '').trim();
  const key = normalized.toUpperCase();

  // Compatibilidade com a chave histórica usada pelo validador interno.
  if (key === 'DINERSCLUB' || key === 'DINNERSCLUB') {
    return 'DinnersClub';
  }

  if (key === 'MASTERCARDS') {
    return 'Mastercard';
  }

  return normalized;
};

/**
 * Normaliza uma lista de bandeiras.
 *
 * @param {Array<*>} cards Lista.
 * @returns {string[]} Lista normalizada.
 */
const normalizeCardList = (cards) => cards.map(normalizeCardName);

/**
 * Configura máscara, ícones e validação de cartão de crédito.
 *
 * `acceptedCards` e `rejectedCards` são consumidos da configuração e armazenados
 * diretamente no input. A validação `checkCreditCard()` é executada em keyup e
 * blur.
 *
 * @param {HTMLInputElement} elem Input base.
 * @param {Object} options Opções recebidas pelo componente.
 * @param {string[]} [options.acceptedCards] Bandeiras permitidas. Default: Visa, Mastercard, Amex, Elo, Hipercard, DinersClub, Discover e JCB.
 * @param {string[]} [options.rejectedCards=[]] Bandeiras explicitamente rejeitadas.
 * @returns {Array.<HTMLInputElement|Object>} Input e o mesmo objeto `options` normalizado.
 * @see module:components/create/input
 */
export default (elem, options) => {
  options.type = 'text';
  options.suffix = { icon: { type: 'ep', name: 'cc' } };
  options.mask = { template: 'creditCard' };

  if (options.label === undefined) {
    options.label = dic.labels.creditCard;
  }

  if (options.prefix === undefined) {
    options.prefix = { icon: { name: 'credit-card', type: 'solid' } };
  }

  if (options.acceptedCards !== undefined && typeof options.acceptedCards === 'object' && Array.isArray(options.acceptedCards)) {
    elem.acceptedCards = normalizeCardList(options.acceptedCards);
    delete options.acceptedCards;
  } else {
    elem.acceptedCards = normalizeCardList(['Visa', 'Mastercard', 'Amex', 'Elo', 'Hipercard', 'DinersClub', 'Discover', 'JCB']);
  }

  if (options.rejectedCards !== undefined && typeof options.rejectedCards === 'object' && Array.isArray(options.rejectedCards)) {
    elem.rejectedCards = normalizeCardList(options.rejectedCards);
    delete options.rejectedCards;
  } else {
    elem.rejectedCards = [];
  }

  elem.addEventListener('keyup', (e) => {
    e.target.checkCreditCard({
      acceptedCards: e.target.acceptedCards,
      rejectedCards: e.target.rejectedCards
    });
  });

  elem.addEventListener('blur', (e) => {
    e.target.checkCreditCard({
      acceptedCards: e.target.acceptedCards,
      rejectedCards: e.target.rejectedCards
    });
  });

  return [elem, options];
};
